import { useStore } from '@doltech/core/lib/entity/store';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import {
  DictationType,
  DUAL_TOGGLE,
  wordDictationFont,
} from '@doltech/domains/dictation/shared/constant';
import { SectionAudioQuizLayout } from '@doltech/domains/dictation/shared/layouts/SectionAudioQuizLayout';
import { FooterQuiz } from '@doltech/domains/dictation/ui/components/FooterQuiz/FooterQuiz';
import { InputDictationStudent } from '@doltech/domains/dictation/ui/components/InputDictationStudent/InputDictationStudent';
import { NavbarLanding } from '@doltech/domains/dictation/ui/components/NavbarLanding/NavbarLanding';
import { Player } from '@doltech/domains/dictation/ui/components/Player/Player';
import { SectionHighlightVocabulary } from '@doltech/domains/dictation/ui/components/SectionHighlightVocabulary/SectionHighlightVocabulary';
import { SectionViewScriptDetail } from '@doltech/domains/dictation/ui/components/SectionViewScriptDetail/SectionViewScriptDetail';
import { StudentDictationQuestionList } from '@doltech/domains/dictation/ui/components/StudentDictationQuestionList/StudentDictationQuestionList';
import { TitleQuiz } from '@doltech/domains/dictation/ui/components/TitleQuiz/TitleQuiz';
import { StudentDictationSidebarLayout } from '@doltech/domains/dictation/shared/layouts/StudentDictationSidebarLayout';
import { DynamicPageProps } from '@doltech/routeDefinition';
import { useUserAuthentication } from '@doltech/ui/lib/figma/Auth/hocs/withUserAuthentication';
import { LayoutPageHeaderOnly } from '@doltech/ui/lib/layouts/pages/LayoutPageHeaderOnly';
import {
  DictationSentenceStatusEnum,
  DictationWordStatusEnum,
  DictationWordTypeEnum,
  PageTypeEnum,
} from '@doltech/utils/lib/constants';
import { flexGap } from '@doltech/utils/lib/css-style';
import { calculateTextLength } from '@doltech/utils/lib/text';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useMutation } from 'react-query';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import { startDoDictation } from '../../api/dictation';
import { submitUserDictation } from '../../api/userDictation';
import { DoDictationStoreInstance } from '../../models/do-dictation.store';
import { useSimpleAudioPlayInBackgroundStore } from '@doltech/ui/lib/hooks/simple-audio-play-in-background.store';
import { InputFreeTextDictationStudent } from '@doltech/domains/dictation/ui/components/InputFreeTextDictationStudent/InputFreeTextDictationStudent';
import { PronunciationProgressButton } from '@doltech/domains/dictation/shared/ui/PronunciationProgressButton/PronunciationProgressButton';
import { FooterFreeTextType } from '@doltech/domains/dictation/ui/components/FooterFreeTextType/FooterFreeTextType';
import { isBrowser } from '@doltech/utils/lib/platform';

const Shadowing = styled.div`
  display: flex;
  align-items: center;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 24px;
`;

const Layout = styled.div`
  max-width: 712px;
  margin: 0 auto;
`;

const Container = styled.div`
  padding: 16px 0;

  ${fromScreen(600)} {
    padding: 24px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 8px 20px 24px;

  ${flexGap([8, 8])}
`;

interface DoDictationAudioBeforeLoginProps
  extends React.HTMLAttributes<HTMLDivElement>,
    DynamicPageProps {}

export const DoDictationAudioBeforeLogin = observer((props: DoDictationAudioBeforeLoginProps) => {
  const { data, internalPageLink, seoData } = props;
  const router = useRouter();
  const { dictation: dictationStore } = useStore<{ dictation: DoDictationStoreInstance }>();
  const { forceLoginToProceedAction } = useUserAuthentication();
  const [questionListVisible, setQuestionListVisible] = React.useState(
    (isBrowser() && window?.innerWidth >= 1280) || false
  );
  const audioStore = useSimpleAudioPlayInBackgroundStore();

  const { landingDictation } = data;

  const {
    name,
    type,
    noOfSentences,
    sourceFile,
    currentSentence,
    sentences,
    currentSentenceIndex,
    jumpToSentenceByIndex,
    next,
    prev,
    updateAllDualToggle,
  } = dictationStore;

  const { reset, isUntouched, canCheck } = currentSentence ?? {};

  useEffectOnce(() => {
    dictationStore.init(data.dictation);
  });

  const startMutate = useMutation(async (dictationId: string) => {
    const res = await startDoDictation(dictationId);
    return res.data;
  });

  const startDictation = async () => {
    const { id: userDictationId } = await startMutate.mutateAsync(data.dictation.id);
    if (userDictationId) {
      dictationStore.init(data.dictation, dictationStore.preparePayload().userAnswers);
      dictationStore.setTimeTakenInSeconds(data?.timeTakenInSeconds || 0);
    }

    return {
      userDictationId,
    };
  };

  const submit = useMutation(
    (userDictationId: string) => {
      return submitUserDictation(userDictationId, dictationStore.preparePayload());
    },
    {
      onSuccess: () => {
        router.replace(landingDictation.url);
      },
    }
  );

  const createUserDictation = async () => {
    const { userDictationId } = await startDictation();
    submit.mutate(userDictationId);
  };

  return (
    <LayoutPageHeaderOnly
      key={currentSentenceIndex}
      header={
        <NavbarLanding
          title={name}
          type={DictationType[type]}
          totalQuestions={noOfSentences}
          onClose={() => {
            router.replace(internalPageLink.staticPageLink?.[PageTypeEnum.LANDING_DICTATION]?.url);
          }}
          onToggleQuestionList={() => setQuestionListVisible((o) => !o)}
          seoUrl={seoData?.url}
          pageId={seoData?.id}
        />
      }
    >
      <Container>
        <Layout>
          <StudentDictationSidebarLayout visible={questionListVisible}>
            <StudentDictationQuestionList
              questions={(sentences as any).map(({ status, key }) => {
                return {
                  key: key,
                  questionNumber: key + 1,
                  state:
                    status === DictationSentenceStatusEnum.IN_PROGRESS ? 'incorrect' : 'default',
                  selected: currentSentenceIndex === key,
                };
              })}
              visible={questionListVisible}
              setVisible={setQuestionListVisible}
              goToQuestion={jumpToSentenceByIndex}
            />
          </StudentDictationSidebarLayout>
          <MainContent>
            <SectionAudioQuizLayout
              header={
                <TitleQuiz
                  totalQuestion={noOfSentences}
                  blankWordCount={currentSentence?.blankWordsCount}
                  currentQuestion={currentSentenceIndex + 1}
                  dualToggle={currentSentence?.dualToggle}
                  isBackNextVisible
                  isToggleVisible
                  onChangeToggle={(toggleType) => updateAllDualToggle(toggleType)}
                  next={next}
                  prev={prev}
                />
              }
              player={
                <Player
                  url={sourceFile?.url}
                  startTimeInSeconds={currentSentence?.start / 1000}
                  endTimeInSeconds={currentSentence?.end / 1000}
                  hasLoopTimes
                />
              }
              body={
                currentSentence?.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT ? (
                  <Body>
                    {currentSentence?.words.map((word) => {
                      const isBlank = word.type === DictationWordTypeEnum.BLANK;
                      const persistedWord = currentSentence.getWordById(word.id);
                      return (
                        <InputDictationStudent
                          status={
                            isBlank
                              ? DictationWordStatusEnum.UNTOUCHED
                              : DictationWordStatusEnum.READ_ONLY
                          }
                          value={isBlank ? persistedWord.studentValue : word.value}
                          onInputChange={(value) => {
                            if (!currentSentence.isInProgress) {
                              currentSentence.setInProgress();
                            }
                            persistedWord.setStudentValue(value);
                            currentSentence.setChecked(false);
                          }}
                          wordWidth={calculateTextLength(word.value, wordDictationFont)}
                        />
                      );
                    })}
                  </Body>
                ) : (
                  <Body>
                    <InputFreeTextDictationStudent
                      isIconHidden
                      value={currentSentence?.freeTextValue || ''}
                      onChange={(value) => {
                        if (!currentSentence.isInProgress) {
                          currentSentence.setInProgress();
                        }
                        currentSentence.setChecked(false);
                        currentSentence?.setFreeTextValue?.(value);
                        currentSentence?.setIsFreeTextIncorrect(false);
                      }}
                      onCheck={() => forceLoginToProceedAction(() => createUserDictation())}
                      highlight={null}
                    />
                  </Body>
                )
              }
              footer={
                currentSentence?.dualToggle === DUAL_TOGGLE.FREE_TEXT ? (
                  <FooterFreeTextType
                    onCheck={() => forceLoginToProceedAction(() => createUserDictation())}
                    isRedoDisabled={!currentSentence?.freeTextValue}
                    isCheckDisabled={!canCheck}
                    words={currentSentence?.words}
                    highlightText={[]}
                    wordsChecked={[]}
                  />
                ) : (
                  <FooterQuiz
                    onCheck={() => forceLoginToProceedAction(() => createUserDictation())}
                    onRedo={reset}
                    isRedoDisabled={isUntouched}
                    isCheckDisabled={!canCheck}
                  />
                )
              }
            />

            <SectionViewScriptDetail
              title="Script, pronunciation & translate"
              words={currentSentence?.words}
              viSub={currentSentence?.contentVi}
              shadowing={
                <Shadowing>
                  <PronunciationProgressButton
                    status="not-yet"
                    onDoPronunciation={() => forceLoginToProceedAction(() => createUserDictation())}
                  />
                </Shadowing>
              }
              handleReadTerm={(term) => audioStore.playByText(term)}
            />

            {Boolean(currentSentence?.popularWords.length) && (
              <SectionHighlightVocabulary popularWords={currentSentence?.popularWords} />
            )}
          </MainContent>
        </Layout>
      </Container>
    </LayoutPageHeaderOnly>
  );
});
